import React, { useContext, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import OperationContext from '../context';
import BasicRadioGroup from '../../../../shared/components/BasicRadioGroup';
import { Button } from '../../../../shared/components';
import Context from '../../../App/MainProvider/context';
import { BasicNotification } from '../../../../shared/components/Notification';
import { findMapped, findMapId, customDateStr, isDraft } from '../../../../shared/misc/utils';
import { FlowType, OperFlowService } from '../../../../services/types/Flow';
import { LanguageType } from '../../../../types';
import FlowRelatedSelection from './FlowRelatedSelection';
import Role from '../../../../types/role';

interface Props {
  hasToolkitEsrr: boolean;
  hasIndicators: boolean;
  loader: (value: boolean) => void;
  reload: () => void;
  operFlowService: OperFlowService;
  flowType: FlowType;
  flowTypeName?: string;
  languages?: LanguageType[];
  milestoneCreateConditionDate: string;
}

const FlowStart: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const {
    hasToolkitEsrr,
    hasIndicators,
    loader,
    reload,
    operFlowService,
    flowType,
    flowTypeName = flowType,
    languages = ['en', 'es'],
    milestoneCreateConditionDate,
  } = props;
  const { operation } = useContext(OperationContext);
  const { user, service, inSuperUserMode } = useContext(Context);
  const [lang, setLang] = useState('');
  const [relatedIds, setRelatedIds] = useState<number[]>([]);

  const radItems = languages
    .map((l, i) => ({ id: i, text: t(`languageCodes.${l}`), htmlId: `rad${l}`, mapped: l }))
    .sort((a, b) => {
      if (a.text > b.text) {
        return 1;
      }
      if (a.text < b.text) {
        return -1;
      }
      return 0;
    });

  const setLanguage = (radId: number) => {
    const value = findMapped(radItems, radId);
    setLang(value);
  };

  const createFlow = () => {
    const dto = { operationId: operation.id, language: lang, relatedOperationIds: relatedIds };
    service(() => operFlowService.create(dto), { callback: reload, loader });
  };

  const maxRelated = () =>
    (flowType === 'INITIAL_ESRS' || flowType === 'ESRS' || flowType === 'SR_ESPF') &&
    relatedIds.length > 4;

  const canCreate =
    (operation.isMember ||
      operation.userTaskDelegations.includes(`${flowType}_CREATE`) ||
      ((flowType === 'INITIAL_ESRS' || flowType === 'ESRS') &&
        (user.roleCode === Role.GroupHead || user.roleCode === Role.HSO)) ||
      inSuperUserMode()) &&
    !isDraft(operation);

  const isSR = flowType === 'SR' || flowType === 'SR_ESPF';

  const flowCreateCondition = () => {
    if (milestoneCreateConditionDate) {
      return t(`flowCreateConditionMilestone.${flowType}`, {
        date: customDateStr(milestoneCreateConditionDate),
      });
    }
    return !hasIndicators
      ? t('noIndicatorsValidation', { number: operation.number, flowType: flowTypeName })
      : t('operationNoFlow', { number: operation.number, flowType: flowTypeName });
  };

  const topMessage = canCreate
    ? flowCreateCondition()
    : t('operationNoFlow', { number: operation.number, flowType: flowTypeName });

  return (
    <div className="m-5">
      <div className="m-3 d-flex justify-content-center">
        <div className="circle-base-big">
          <div className="circle-inner">
            <FontAwesomeIcon icon="file-word" />
          </div>
        </div>
      </div>
      <div className="m-3 d-flex justify-content-center">
        <h1 className="page-title inner-title">{topMessage}</h1>
      </div>
      {canCreate && hasToolkitEsrr && hasIndicators && (
        <>
          {!isSR && (
            <div className="notification-wrapper mx-2 mt-n1 d-flex justify-content-center pl-3">
              <BasicNotification
                message={t('flowToolkitEsrrUpdateMessage', { flowType: flowTypeName })}
                color="info"
                icon="exclamation-triangle"
              />
            </div>
          )}
          <div className="m-3 d-flex justify-content-center">
            <p>{t('chooseLanguage')}</p>
          </div>

          <div className="m-3 d-flex justify-content-center pl-3">
            <BasicRadioGroup
              name="select"
              elements={radItems}
              onChange={setLanguage}
              value={findMapId(radItems, lang)}
            />
          </div>
          <div className="mx-3 d-flex justify-content-center">
            <FlowRelatedSelection
              setRelatedOperationIds={(values) => setRelatedIds(values)}
              flowType={flowType}
            />
          </div>
          <div className="m-3 d-flex justify-content-center">
            <Button
              text={t('createFlow', { flowType: flowTypeName })}
              onClick={createFlow}
              className="btn-primary"
              disabled={!lang || maxRelated()}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FlowStart;
